"use client";

import { useCallback, useEffect, useState } from "react";
import { Building2, Loader2, MapPin } from "lucide-react";
import posthog from "posthog-js";
import {
  readChosenTenantSlug,
  storeChosenTenantSlug,
  tenantUrl,
} from "@/lib/tenant/entry-binding";

// Pantalla inicial de configuración de la app de operadores (/ingresar).
//
// Si el dispositivo ya eligió municipio (cookie) va DERECHO a su subdominio.
// Si no, dos caminos: (1) "Usar mi ubicación" -> GPS -> point-in-polygon contra
// las zonas de los municipios; (2) escribir el municipio -> match difuso. Ambos
// pegan a /api/tenant-entry (tenant-agnóstico). El fallback de texto SIEMPRE
// está: el GPS del revisor de Google cae fuera de todo municipio argentino, así
// que sin este campo la review se traba.

type Mode = "checking" | "idle" | "locating";

interface Hit {
  slug: string;
  name: string;
}

export function TenantEntryScreen() {
  const [mode, setMode] = useState<Mode>("checking");
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Ya vinculado -> derecho a su municipio (navegación de página completa: el
  // destino es OTRO subdominio y salta dentro del webview por allowNavigation).
  useEffect(() => {
    const slug = readChosenTenantSlug();
    if (slug) {
      window.location.assign(tenantUrl(slug));
      return;
    }
    setMode("idle");
  }, []);

  const goToTenant = useCallback((hit: Hit, via: "location" | "typed") => {
    posthog.capture("operator_tenant_selected", { via });
    storeChosenTenantSlug(hit.slug);
    window.location.assign(tenantUrl(hit.slug));
  }, []);

  const useLocation = useCallback(() => {
    setError(null);
    if (!("geolocation" in navigator)) {
      setError("Este dispositivo no tiene ubicación. Escribí tu municipio abajo.");
      return;
    }
    setMode("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch("/api/tenant-entry", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }),
          });
          if (res.ok) {
            goToTenant((await res.json()) as Hit, "location");
            return;
          }
          setMode("idle");
          setError(
            "No pudimos detectar tu municipio por la ubicación. Escribilo abajo.",
          );
        } catch {
          setMode("idle");
          setError("Hubo un problema. Escribí tu municipio abajo.");
        }
      },
      () => {
        setMode("idle");
        setError(
          "No pudimos usar tu ubicación (permiso denegado). Escribí tu municipio abajo.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [goToTenant]);

  const submitQuery = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/tenant-entry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q }),
      });
      if (res.ok) {
        goToTenant((await res.json()) as Hit, "typed");
        return;
      }
      setError(
        "No encontramos ese municipio. Revisá cómo lo escribiste o probá con la ubicación.",
      );
    } catch {
      setError("Hubo un problema. Probá de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }, [query, goToTenant]);

  if (mode === "checking") {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-[var(--color-bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-fg-muted)]" />
      </div>
    );
  }

  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-[var(--color-bg)] px-6 py-10 text-center text-[var(--color-fg)]">
      <div className="space-y-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">
          Vortigon
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Configurá la aplicación
        </h1>
        <p className="max-w-xs text-sm text-[var(--color-fg-muted)]">
          Elegí tu municipio. Se hace una sola vez: después, la app abre directo
          en tu central.
        </p>
      </div>

      <button
        type="button"
        onClick={useLocation}
        disabled={mode === "locating"}
        className="inline-flex h-12 w-full max-w-xs items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-6 text-base font-semibold text-[var(--color-accent-fg)] shadow-sm hover:opacity-90 disabled:opacity-60"
      >
        {mode === "locating" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Detectando…
          </>
        ) : (
          <>
            <MapPin className="h-5 w-5" /> Usar mi ubicación
          </>
        )}
      </button>

      {error ? (
        <p className="max-w-xs text-sm text-amber-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="w-full max-w-xs space-y-2">
        <div className="flex items-center gap-3 text-xs text-[var(--color-fg-subtle)]">
          <span className="h-px flex-1 bg-[var(--color-border)]" />o escribí tu municipio
          <span className="h-px flex-1 bg-[var(--color-border)]" />
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="text"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitQuery();
            }}
            placeholder="Ej: Bariloche"
            className="h-11 min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-fg)] placeholder:text-[var(--color-fg-subtle)]"
          />
          <button
            type="button"
            onClick={() => void submitQuery()}
            disabled={submitting || query.trim().length < 2}
            className="inline-flex h-11 items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 text-sm font-medium text-[var(--color-fg)] hover:bg-[var(--color-bg-muted)] disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            Entrar
          </button>
        </div>
      </div>
    </main>
  );
}
